import * as THREE from 'three';
import type { FogState } from '../../../shared/game/fog-types.js';
import type { MapDefinition, Point } from '../../../shared/game/world-types.js';
import { SightLines, obstaclesNear } from '../../../shared/game/vision.js';
import { levelMap } from '../../../shared/game/caves.js';

const MAX_SOURCES = 16;

// One set of uniforms shared by every fogged material, so a fog update reaches them all without touching a material.
const uniforms = {
  fowOn: { value: 0 }, fowMask: { value: null as THREE.Texture | null }, fowOcclusion: { value: null as THREE.Texture | null },
  fowSize: { value: new THREE.Vector2(1, 1) }, fowOrigin: { value: new THREE.Vector2() }, fowMap: { value: new THREE.Vector2(1, 1) },
  fowSources: { value: Array.from({ length: MAX_SOURCES }, () => new THREE.Vector2()) }, fowAnchors: { value: Array.from({ length: MAX_SOURCES }, () => new THREE.Vector2()) }, fowCount: { value: 0 },
  fowWindow: { value: 1 }, fowTile: { value: 1 }, fowRadius: { value: 1 }, fowTime: { value: 0 }, fowColor: { value: new THREE.Color(0x070d10) },
};
/**
 * What the shaders receive: the two masks and one small float texture holding everything else (`pack`). Three uploads a material's
 * uniforms whenever consecutive draws switch material, and the source and anchor arrays (32 vectors) cost about a tenth of a crowded
 * frame; a texture binding is cheap, and its few hundred bytes upload once a frame. The values, and so the picture, are the same.
 */
const DATA_TEXELS = MAX_SOURCES + 4;
const data = new Float32Array(DATA_TEXELS * 4);
const dataTexture = new THREE.DataTexture(data, DATA_TEXELS, 1, THREE.RGBAFormat, THREE.FloatType);
dataTexture.minFilter = dataTexture.magFilter = THREE.NearestFilter; dataTexture.generateMipmaps = false;
const shaderUniforms = { fowMask: uniforms.fowMask, fowOcclusion: uniforms.fowOcclusion, fowData: { value: dataTexture } };
function pack() {
  for (let i = 0; i < MAX_SOURCES; i++) data.set([uniforms.fowSources.value[i].x, uniforms.fowSources.value[i].y, uniforms.fowAnchors.value[i].x, uniforms.fowAnchors.value[i].y], i * 4);
  data.set([uniforms.fowOn.value, uniforms.fowWindow.value, uniforms.fowTile.value, uniforms.fowRadius.value], MAX_SOURCES * 4);
  data.set([uniforms.fowTime.value, uniforms.fowCount.value, uniforms.fowOrigin.value.x, uniforms.fowOrigin.value.y], (MAX_SOURCES + 1) * 4);
  data.set([uniforms.fowSize.value.x, uniforms.fowSize.value.y, uniforms.fowMap.value.x, uniforms.fowMap.value.y], (MAX_SOURCES + 2) * 4);
  data.set([uniforms.fowColor.value.r, uniforms.fowColor.value.g, uniforms.fowColor.value.b, 0], (MAX_SOURCES + 3) * 4);
  dataTexture.needsUpdate = true;
}

const PARS = `varying vec3 vFowWorld;
uniform sampler2D fowMask; uniform sampler2D fowOcclusion; uniform highp sampler2D fowData;
vec4 fowAt(int i) { return texelFetch(fowData, ivec2(i, 0), 0); }
#define fowOn fowAt(${MAX_SOURCES}).x
#define fowWindow fowAt(${MAX_SOURCES}).y
#define fowTile fowAt(${MAX_SOURCES}).z
#define fowRadius fowAt(${MAX_SOURCES}).w
#define fowTime fowAt(${MAX_SOURCES + 1}).x
#define fowCount int(fowAt(${MAX_SOURCES + 1}).y)
#define fowOrigin fowAt(${MAX_SOURCES + 1}).zw
#define fowSize fowAt(${MAX_SOURCES + 2}).xy
#define fowMap fowAt(${MAX_SOURCES + 2}).zw
#define fowColor fowAt(${MAX_SOURCES + 3}).rgb
float fowHash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float fowNoise(vec2 p){ vec2 i = floor(p), f = fract(p); f = f * f * (3. - 2. * f);
  return mix(mix(fowHash(i), fowHash(i + vec2(1., 0.)), f.x), mix(fowHash(i + vec2(0., 1.)), fowHash(i + vec2(1., 1.)), f.x), f.y); }
float fowFbm(vec2 p){ float v = 0., a = .5; for (int i = 0; i < 4; i++) { v += a * fowNoise(p); p = p * 2.03 + vec2(17.3, 9.1); a *= .5; } return v; }
// 1 where the hero has a clear line to the ground, 0 in the shadow of a solid obstacle or behind a cliff rising above them.
float fowLit(int i, vec2 p){
  vec2 local = (p - fowAt(i).zw) / fowTile + fowWindow * .5;
  if (local.x < 0. || local.y < 0. || local.x >= fowWindow || local.y >= fowWindow) return 0.;
  return smoothstep(.35, .65, texture2D(fowOcclusion, vec2(local.x, local.y + float(i) * fowWindow) / vec2(fowWindow, fowWindow * ${MAX_SOURCES}.)).r);
}
vec3 fowApply(vec3 colour, vec3 world){
  if (fowOn < .5 || abs(world.x) > fowMap.x * .5 || abs(world.z) > fowMap.y * .5) return colour;
  // Two layers drifting in different directions, so the fog rolls rather than slides.
  vec2 drift = vec2(fowTime * .55, fowTime * .3), at = world.xz;
  float slow = fowFbm(at * .045 + drift * .12), swirl = fowFbm(at * .11 - drift.yx * .2 + slow * 2.);
  float billow = mix(slow, swirl, .5) - .5;
  vec2 warped = at + vec2(billow, fowFbm(at * .09 + 5. + drift * .1) - .5) * 1.5;
  float explored = smoothstep(.3, .7, texture2D(fowMask, (warped - fowOrigin) / fowSize).r); // bilinear + threshold rounds tile edges
  float seen = 0.;
  for (int i = 0; i < ${MAX_SOURCES}; i++) { if (i >= fowCount) break; seen = max(seen, (1. - smoothstep(fowRadius - 5., fowRadius + 1., distance(warped, fowAt(i).xy))) * fowLit(i, warped)); }
  float density = mix(.94, .5, explored) * (1. - seen) * (.8 + .4 * (swirl + billow));
  return mix(colour, fowColor + vec3(.05, .07, .09) * smoothstep(.3, .8, swirl), clamp(density, 0., .97));
}
`;
// Instanced and batched copies each sit at their own place.
const VERTEX = `vec4 fowPosition = vec4(transformed, 1.);
#ifdef USE_BATCHING
  fowPosition = batchingMatrix * fowPosition;
#endif
#ifdef USE_INSTANCING
  fowPosition = instanceMatrix * fowPosition;
#endif
vFowWorld = (modelMatrix * fowPosition).xyz;`;

const adopted = new WeakSet<THREE.Material>();
/** Whether fog of war has already patched `material`'s compile hook; a copy that keeps the hook must then opt out (`noFogOfWar`). */
export const fogAdopted = (material: THREE.Material) => adopted.has(material);
const fogged = (material: THREE.Material) => !material.userData.noFogOfWar && material.depthTest !== false && ((material as THREE.MeshStandardMaterial).isMeshStandardMaterial
  || (material as THREE.MeshLambertMaterial).isMeshLambertMaterial || (material as THREE.MeshPhongMaterial).isMeshPhongMaterial
  || (material as THREE.MeshBasicMaterial).isMeshBasicMaterial || (material as THREE.MeshToonMaterial).isMeshToonMaterial
  || (material as THREE.PointsMaterial).isPointsMaterial || (material as THREE.SpriteMaterial).isSpriteMaterial);

/**
 * Every surface is fogged at its own world position: a tower, a tree or a cliff face is dark or clear by where it stands, never by
 * the ground drawn behind it. Built-in materials are patched as they appear in the scene; custom shaders opt in with `withFogOfWar`.
 */
function adopt(material: THREE.Material) {
  adopted.add(material);
  if (!fogged(material)) return;
  const previous = material.onBeforeCompile, previousKey = material.customProgramCacheKey;
  // The default key is the compile hook's source, which this wrapper would replace with its own.
  const key = previousKey === THREE.Material.prototype.customProgramCacheKey ? () => previous.toString() : () => previousKey.call(material);
  material.onBeforeCompile = (shader, renderer) => {
    previous.call(material, shader, renderer);
    Object.assign(shader.uniforms, shaderUniforms);
    // At the end of main, after any hook that replaced the standard projection (wind sway does).
    const end = shader.vertexShader.lastIndexOf('}');
    // A sprite has no transformed vertex: it stands at its object's origin.
    const world = (material as THREE.SpriteMaterial).isSpriteMaterial ? 'vFowWorld = (modelMatrix * vec4(0., 0., 0., 1.)).xyz;' : VERTEX;
    shader.vertexShader = 'varying vec3 vFowWorld;\n' + shader.vertexShader.slice(0, end) + world + '\n}';
    shader.fragmentShader = PARS + shader.fragmentShader.replace('#include <fog_fragment>', '#include <fog_fragment>\n  gl_FragColor.rgb = fowApply(gl_FragColor.rgb, vFowWorld);');
  };
  material.customProgramCacheKey = () => key() + '|fog-of-war';
  material.needsUpdate = true;
}

/** Fogs a custom shader: its vertex shader must use `position` and `modelMatrix`, and its fragment shader end by writing `gl_FragColor`. */
export function withFogOfWar<T extends THREE.ShaderMaterial>(material: T): T {
  adopted.add(material);
  Object.assign(material.uniforms, shaderUniforms);
  material.vertexShader = 'varying vec3 vFowWorld;\n' + material.vertexShader.replace(/gl_Position\s*=/, 'vFowWorld = (modelMatrix * vec4(position, 1.)).xyz; gl_Position =');
  const end = material.fragmentShader.lastIndexOf('}');
  material.fragmentShader = PARS + material.fragmentShader.slice(0, end) + '  gl_FragColor.rgb = fowApply(gl_FragColor.rgb, vFowWorld);\n}';
  return material;
}

/** Dark fog over unexplored ground and a lighter one over explored-but-unseen ground. Presentation only. */
function adoptTree(root: THREE.Object3D) {
  root.traverse(object => {
    const material = (object as THREE.Mesh).material;
    if (!material) return;
    for (const item of Array.isArray(material) ? material : [material]) if (!adopted.has(item)) adopt(item);
  });
}
/**
 * Scenes whose additions are adopted as they are added. Adopting changes a material's program, so a material drawn before adoption
 * linked its shaders twice, mid-fight (an arrow, a stain, a burst); adopting the added subtree at once gives it the fogged program
 * warming already compiled.
 */
const watched = new Set<THREE.Object3D>();
const add = THREE.Object3D.prototype.add;
let watching = false;
function watchAdditions(scene: THREE.Scene) {
  watched.add(scene);
  if (watching) return;
  watching = true;
  THREE.Object3D.prototype.add = function (this: THREE.Object3D, ...objects: THREE.Object3D[]) {
    const result = add.apply(this, objects);
    if (watched.size) {
      let root: THREE.Object3D = this;
      while (root.parent) root = root.parent;
      if (watched.has(root)) for (const object of objects) adoptTree(object);
    }
    return result;
  };
}
/** A material swapped onto an object already in the scene is not an addition: a full pass every this many snapshots adopts it. */
const SWEEP_SNAPSHOTS = 30;

export class FogOfWarOverlay {
  private texture: THREE.DataTexture | null = null;
  private revision = -1;
  // One square window of source-centred line-of-sight samples per hero, stacked vertically.
  private occlusion: THREE.DataTexture | null = null;
  private occlusionData = new Uint8Array(0);
  private shadowed: (Point & { level?: number })[] = [];
  private window = 0;
  private lastTick: number | null = null;
  private sweep = 0;
  readonly uniforms = uniforms;

  constructor(private scene: THREE.Scene, private map: MapDefinition) {
    uniforms.fowOrigin.value.set(-map.width / 2, -map.depth / 2); uniforms.fowMap.value.set(map.width, map.depth);
    uniforms.fowOn.value = 0; pack();
    watchAdditions(scene);
  }

  get visible() { return uniforms.fowOn.value > .5; }
  set visible(on: boolean) { if (uniforms.fowOn.value !== (on ? 1 : 0)) { uniforms.fowOn.value = on ? 1 : 0; pack(); } }

  /** Advances the drifting fog; called every frame with seconds. */
  tick(seconds: number) {
    // Presentation time does not jump several seconds after a suspended tab or a slow GPU call.
    uniforms.fowTime.value = this.lastTick === null ? seconds : uniforms.fowTime.value + Math.max(0, Math.min(.1, seconds - this.lastTick));
    this.lastTick = seconds; pack();
  }

  /** Patches every material now in the scene. Call before compiling shaders, so warming builds the fogged programs the game draws with. */
  adoptScene() { adoptTree(this.scene); this.sweep = 0; }

  update(fog: FogState) {
    if (++this.sweep >= SWEEP_SNAPSHOTS) this.adoptScene();
    const sources = fog.sources.slice(0, MAX_SOURCES);
    sources.forEach((source, index) => uniforms.fowSources.value[index].set(source.x, source.z));
    uniforms.fowCount.value = sources.length; uniforms.fowRadius.value = fog.visionRadius;
    this.updateOcclusion(sources, fog); pack();
    if (fog.revision === this.revision) return;
    this.revision = fog.revision;
    // The mask covers the whole map (~2 MB), and revisions arrive whenever the party uncovers ground. Rewriting one
    // buffer and texture in place keeps that off the garbage collector, and the GPU storage is allocated only once.
    if (!this.texture || this.texture.image.width !== fog.columns || this.texture.image.height !== fog.rows) {
      this.texture?.dispose();
      this.texture = new THREE.DataTexture(new Uint8Array(fog.columns * fog.rows), fog.columns, fog.rows, THREE.RedFormat, THREE.UnsignedByteType);
      this.texture.minFilter = this.texture.magFilter = THREE.LinearFilter;
      uniforms.fowMask.value = this.texture;
      uniforms.fowSize.value.set(fog.columns * fog.tileSize, fog.rows * fog.tileSize);
    }
    const data = this.texture.image.data as Uint8Array;
    data.fill(0);
    for (let run = 0; run < fog.explored.length; run += 2) data.fill(255, fog.explored[run], fog.explored[run + 1]);
    this.texture.needsUpdate = true; pack();
  }

  /** Re-samples the vision shadows only when a hero has moved. Same rule the server uses for exploration. */
  private updateOcclusion(sources: (Point & { level?: number })[], fog: FogState) {
    if (sources.length === this.shadowed.length && sources.every((source, i) => source.x === this.shadowed[i].x && source.z === this.shadowed[i].z && source.level === this.shadowed[i].level)) return;
    this.shadowed = sources.map(({ x, z, level }) => ({ x, z, level }));
    const size = fog.tileSize, window = Math.ceil(fog.visionRadius / size) * 2 + 2;
    if (window !== this.window) {
      this.window = window; this.occlusionData = new Uint8Array(window * window * MAX_SOURCES);
      this.occlusion?.dispose();
      this.occlusion = new THREE.DataTexture(this.occlusionData, window, window * MAX_SOURCES, THREE.RedFormat, THREE.UnsignedByteType);
      this.occlusion.minFilter = this.occlusion.magFilter = THREE.LinearFilter;
      uniforms.fowOcclusion.value = this.occlusion; uniforms.fowWindow.value = window; uniforms.fowTile.value = size;
    }
    this.occlusionData.fill(0);
    sources.forEach((source, index) => {
      // Samples sit on the fog's own tile centres, not around the hero, so a sharp edge such as a cliff rim does not shimmer
      // as the window slides with every step.
      const anchor = { x: Math.round((source.x + this.map.width / 2) / size) * size - this.map.width / 2, z: Math.round((source.z + this.map.depth / 2) / size) * size - this.map.depth / 2 };
      uniforms.fowAnchors.value[index].set(anchor.x, anchor.z);
      // A hero sees along the ground of the level it walks: in a cave, not through its rock.
      const sight = new SightLines(source, obstaclesNear(levelMap(this.map, source.level ?? 0), source, fog.visionRadius));
      // The shader's vision falls to nothing a metre past the radius; beyond that and one blended sample more, shadows never show.
      const reach = (fog.visionRadius + 1 + 2 * size) ** 2;
      for (let row = 0; row < window; row++) for (let column = 0; column < window; column++) {
        const point = { x: anchor.x + (column + .5 - window / 2) * size, z: anchor.z + (row + .5 - window / 2) * size };
        if ((point.x - source.x) ** 2 + (point.z - source.z) ** 2 <= reach && sight.visible(point)) this.occlusionData[(index * window + row) * window + column] = 255;
      }
    });
    this.occlusion!.needsUpdate = true;
  }

  dispose() {
    watched.delete(this.scene);
    this.occlusion?.dispose(); this.texture?.dispose();
    uniforms.fowOn.value = 0; uniforms.fowMask.value = uniforms.fowOcclusion.value = null;
  }
}
